import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  FiShield,
  FiFileText,
  FiCheckCircle,
  FiExternalLink,
  FiPower,
  FiAlertTriangle,
  FiGlobe,
  FiUsers,
  FiCompass,
  FiCheckSquare,
  FiLayers,
  FiBookOpen,
  FiRefreshCw,
  FiMail,
  FiCalendar,
  FiUser,
  FiSettings,
  FiChevronRight,
  FiArrowUp,
  FiList
} from 'react-icons/fi';
import './TermsConditions.css';

const TOC_ITEMS = [
  { id: 'interpretation-definitions', label: '1. Interpretation & Definitions', icon: FiFileText },
  { id: 'acknowledgment', label: '2. Acknowledgment', icon: FiCheckCircle },
  { id: 'links-to-other-websites', label: '3. Links to Other Websites', icon: FiExternalLink },
  { id: 'termination', label: '4. Termination', icon: FiPower },
  { id: 'limitation-of-liability', label: '5. Limitation of Liability', icon: FiShield },
  { id: 'as-is-disclaimer', label: '6. "AS IS" Disclaimer', icon: FiAlertTriangle },
  { id: 'governing-law', label: '7. Governing Law', icon: FiGlobe },
  { id: 'dispute-resolution', label: '8. Dispute Resolution', icon: FiUsers },
  { id: 'eu-users', label: '9. EU Users', icon: FiCompass },
  { id: 'us-legal-compliance', label: '10. US Legal Compliance', icon: FiCheckSquare },
  { id: 'severability-waiver', label: '11. Severability and Waiver', icon: FiLayers },
  { id: 'translation-interpretation', label: '12. Translation Interpretation', icon: FiBookOpen },
  { id: 'changes-to-terms', label: '13. Changes to Terms', icon: FiRefreshCw },
  { id: 'contact-us', label: '14. Contact Us', icon: FiMail },
  { id: 'view-other-policies', label: '15. View Other Policies', icon: FiShield },
];

const TermsConditions = () => {
  const [activeSection, setActiveSection] = useState('interpretation-definitions');
  const [showBackToTop, setShowBackToTop] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);

    const handleScroll = () => {
      if (window.scrollY > 400) {
        setShowBackToTop(true);
      } else {
        setShowBackToTop(false);
      }

      // Determine active section based on scroll position
      const scrollPosition = window.scrollY + 180;
      for (let i = TOC_ITEMS.length - 1; i >= 0; i--) {
        const section = document.getElementById(TOC_ITEMS[i].id);
        if (section) {
          const top = section.offsetTop;
          if (scrollPosition >= top) {
            setActiveSection(TOC_ITEMS[i].id);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id) => {
    setActiveSection(id);
    setMobileMenuOpen(false);
    const element = document.getElementById(id);
    if (element) {
      const yOffset = -90; // Offset for navbar
      const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="terms-page-wrapper">
      {/* Background Decorative Mesh Orbs */}
      <div className="terms-bg-ambient" aria-hidden="true">
        <div className="terms-ambient-orb orb-primary"></div>
        <div className="terms-ambient-orb orb-secondary"></div>
        <div className="terms-ambient-orb orb-tertiary"></div>
      </div>

      {/* Hero Header Section */}
      <section className="terms-hero-section">
        <div className="terms-hero-container">
          <div className="terms-hero-content">
            {/* Pill Badge */}
            <div className="terms-hero-badge">
              <FiFileText className="terms-badge-icon" />
              <span>Legal</span>
            </div>

            {/* Gradient Title */}
            <h1 className="terms-hero-title">
              <span className="title-terms">Terms</span> <span className="title-amp">&</span> <span className="title-conditions">Conditions</span>
            </h1>

            {/* Subtitle / Intro Description */}
            <p className="terms-hero-subtitle">
              Please read these terms and conditions carefully before using Our Service. By accessing or using this website, you agree to be bound by these terms.
            </p>

            {/* Metadata Pill */}
            <div className="terms-hero-meta">
              <div className="terms-meta-item">
                <FiCalendar className="terms-meta-icon" />
                <span>
                  <strong>Last Updated On: </strong>28-03-2026
                </span>
              </div>
            </div>
          </div>

          {/* Hero Right Visual: High-tech Contract Sheet & Shield with Checkmark */}
          <div className="terms-hero-visual" aria-hidden="true">
            <div className="terms-shield-canvas">
              {/* Central Glowing Backdrop */}
              <div className="terms-glow-backdrop"></div>

              <svg className="terms-contract-svg" viewBox="0 0 280 280" fill="none" xmlns="http://www.w3.org/2000/svg">
                {/* Orbit track rings */}
                <circle cx="140" cy="140" r="115" stroke="currentColor" strokeWidth="1.5" strokeDasharray="6 6" className="terms-orbit-ring" />
                <circle cx="140" cy="140" r="85" stroke="currentColor" strokeWidth="1" strokeOpacity="0.25" />

                {/* Connector rays */}
                <line x1="140" y1="140" x2="225" y2="70" stroke="currentColor" strokeWidth="1.5" strokeOpacity="0.35" strokeDasharray="3 3" />
                <line x1="140" y1="140" x2="250" y2="155" stroke="currentColor" strokeWidth="1.5" strokeOpacity="0.35" strokeDasharray="3 3" />
                <line x1="140" y1="140" x2="65" y2="205" stroke="currentColor" strokeWidth="1.5" strokeOpacity="0.35" strokeDasharray="3 3" />

                <defs>
                  <linearGradient id="contractPaperGrad" x1="100" y1="55" x2="185" y2="195" gradientUnits="userSpaceOnUse">
                    <stop offset="0%" stopColor="#ffffff" stopOpacity="0.95" />
                    <stop offset="100%" stopColor="#e0e7ff" stopOpacity="0.85" />
                  </linearGradient>
                  <linearGradient id="termsShieldGrad" x1="145" y1="135" x2="195" y2="215" gradientUnits="userSpaceOnUse">
                    <stop offset="0%" stopColor="#38bdf8" />
                    <stop offset="50%" stopColor="#2563eb" />
                    <stop offset="100%" stopColor="#1e3a8a" />
                  </linearGradient>
                  <filter id="contractShadow" x="80" y="45" width="125" height="170" filterUnits="userSpaceOnUse">
                    <feDropShadow dx="0" dy="8" stdDeviation="10" floodColor="#2563eb" floodOpacity="0.25" />
                  </filter>
                  <filter id="termsShieldShadow" x="130" y="120" width="80" height="100" filterUnits="userSpaceOnUse">
                    <feDropShadow dx="0" dy="6" stdDeviation="8" floodColor="#2563eb" floodOpacity="0.4" />
                  </filter>
                </defs>

                {/* Contract Paper Document in Background */}
                <g filter="url(#contractShadow)">
                  <rect x="95" y="55" width="90" height="130" rx="8" fill="url(#contractPaperGrad)" stroke="#38bdf8" strokeWidth="1.5" strokeOpacity="0.5" />
                  {/* Document lines */}
                  <line x1="110" y1="75" x2="160" y2="75" stroke="#3b82f6" strokeWidth="3" strokeLinecap="round" />
                  <line x1="110" y1="90" x2="170" y2="90" stroke="#93c5fd" strokeWidth="2.5" strokeLinecap="round" />
                  <line x1="110" y1="103" x2="170" y2="103" stroke="#93c5fd" strokeWidth="2.5" strokeLinecap="round" />
                  <line x1="110" y1="116" x2="170" y2="116" stroke="#93c5fd" strokeWidth="2.5" strokeLinecap="round" />
                  <line x1="110" y1="129" x2="155" y2="129" stroke="#93c5fd" strokeWidth="2.5" strokeLinecap="round" />
                  <line x1="110" y1="142" x2="140" y2="142" stroke="#cbd5e1" strokeWidth="2.5" strokeLinecap="round" />
                </g>

                {/* Glowing Shield Over Document */}
                <g filter="url(#termsShieldShadow)">
                  <path
                    d="M170 135 L198 147 C198 180 185 204 170 216 C155 204 142 180 142 147 L170 135 Z"
                    fill="url(#termsShieldGrad)"
                    stroke="#60a5fa"
                    strokeWidth="2.5"
                  />
                  {/* Checkmark inside Shield */}
                  <path
                    d="M158 174 L167 183 L183 162"
                    stroke="#ffffff"
                    strokeWidth="3.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </g>
              </svg>

              {/* Floating Node 1: Purple User Circle */}
              <div className="terms-floating-bubble bubble-purple" style={{ top: '18%', right: '14%' }}>
                <FiUser className="bubble-icon" />
              </div>

              {/* Floating Node 2: Blue Legal Balance / Scales Circle */}
              <div className="terms-floating-bubble bubble-blue" style={{ top: '50%', right: '0%' }}>
                <svg className="bubble-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="22" height="22">
                  <path d="M12 3v18" />
                  <path d="M6 8l6-3 6 3" />
                  <path d="M6 8l-3 7a3 3 0 0 0 6 0L6 8" />
                  <path d="M18 8l-3 7a3 3 0 0 0 6 0L18 8" />
                </svg>
              </div>

              {/* Floating Node 3: Blue Gear Circle */}
              <div className="terms-floating-bubble bubble-cyan" style={{ bottom: '15%', left: '12%' }}>
                <FiSettings className="bubble-icon" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content Layout with Sidebar & Content Cards */}
      <div className="terms-body-container">
        {/* Mobile Table of Contents Accordion Toggle */}
        <div className="terms-mobile-toc-toggle">
          <button
            className="mobile-toc-btn"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-expanded={mobileMenuOpen}
          >
            <FiList className="toc-toggle-icon" />
            <span>On This Page: {TOC_ITEMS.find((item) => item.id === activeSection)?.label || 'Sections'}</span>
            <FiChevronRight className={`toc-toggle-arrow ${mobileMenuOpen ? 'open' : ''}`} />
          </button>
        </div>

        {/* Left Column: Sticky Table of Contents */}
        <aside className={`terms-sidebar-nav ${mobileMenuOpen ? 'mobile-visible' : ''}`}>
          <div className="terms-toc-card">
            <h2 className="terms-toc-title">On This Page</h2>
            <nav className="terms-toc-list" aria-label="Table of Contents">
              {TOC_ITEMS.map((item) => {
                const IconComponent = item.icon;
                const isActive = activeSection === item.id;
                return (
                  <button
                    key={item.id}
                    onClick={() => scrollToSection(item.id)}
                    className={`terms-toc-item ${isActive ? 'active' : ''}`}
                    type="button"
                  >
                    <span className="toc-item-indicator" aria-hidden="true" />
                    <IconComponent className="toc-item-icon" />
                    <span className="toc-item-text">{item.label}</span>
                  </button>
                );
              })}
            </nav>
          </div>
        </aside>

        {/* Right Column: Main Content Cards */}
        <main className="terms-content-stream">
          {/* Section 1: Interpretation and Definitions */}
          <section id="interpretation-definitions" className="terms-card">
            <div className="terms-card-header">
              <div className="terms-section-icon-box box-blue">
                <FiFileText className="terms-section-icon" />
              </div>
              <h2 className="terms-card-title">
                1. Interpretation and <span className="title-gradient">Definitions</span>
              </h2>
            </div>

            <div className="terms-card-body">
              <div className="terms-notice-banner">
                <h3 className="terms-notice-title">Please Read Carefully</h3>
                <p className="terms-paragraph" style={{ marginBottom: 0 }}>
                  Please read these terms and conditions carefully before using Our Service.
                </p>
              </div>

              <h3 className="terms-subheading">Interpretation</h3>
              <p className="terms-paragraph">
                The words of which the initial letter is capitalized have meanings defined under the following conditions. The following definitions shall have the same meaning regardless of whether they appear in singular or in plural.
              </p>

              <h3 className="terms-subheading">Definitions</h3>
              <p className="terms-paragraph">For the purposes of these Terms and Conditions:</p>

              <div className="terms-definitions-grid">
                <div className="definition-item">
                  <div className="def-term">Affiliate</div>
                  <div className="def-desc">means an entity that controls, is controlled by or is under common control with a party.</div>
                </div>

                <div className="definition-item">
                  <div className="def-term">Country</div>
                  <div className="def-desc">refers to: Tamil Nadu, India.</div>
                </div>

                <div className="definition-item">
                  <div className="def-term">Company</div>
                  <div className="def-desc">(referred to as either "the Company", "We", "Us" or "Our") refers to Praveen Kumar B Portfolio.</div>
                </div>

                <div className="definition-item">
                  <div className="def-term">Device</div>
                  <div className="def-desc">means any device that can access the Service.</div>
                </div>

                <div className="definition-item">
                  <div className="def-term">Service</div>
                  <div className="def-desc">refers to the Website.</div>
                </div>

                <div className="definition-item">
                  <div className="def-term">Terms and Conditions</div>
                  <div className="def-desc">(also referred as "Terms") mean this agreement.</div>
                </div>

                <div className="definition-item">
                  <div className="def-term">Third-party Social Media Service</div>
                  <div className="def-desc">means any services provided by a third-party and made available through the Service.</div>
                </div>

                <div className="definition-item">
                  <div className="def-term">Website</div>
                  <div className="def-desc">
                    refers to Praveen Kumar B Portfolio, accessible from{' '}
                    <a
                      href="https://praveen-kumar-b.web.app/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="terms-inline-link"
                    >
                      Click to see Website
                    </a>.
                  </div>
                </div>

                <div className="definition-item">
                  <div className="def-term">You</div>
                  <div className="def-desc">means the individual or legal entity accessing or using the Service.</div>
                </div>
              </div>
            </div>
          </section>

          {/* Section 2: Acknowledgment */}
          <section id="acknowledgment" className="terms-card">
            <div className="terms-card-header">
              <div className="terms-section-icon-box box-green">
                <FiCheckCircle className="terms-section-icon" />
              </div>
              <h2 className="terms-card-title">
                2. <span className="title-gradient">Acknowledgment</span>
              </h2>
            </div>

            <div className="terms-card-body">
              <p className="terms-paragraph">
                These Terms govern the use of this Service and form the agreement between You and the Company. If You disagree with any part, You may not access the Service. You must be over 18 to use this Service.
              </p>

              <div className="terms-privacy-callout">
                <FiShield className="callout-shield-icon" />
                <div className="callout-text">
                  <strong>Your use is also conditioned on acceptance of our{' '}
                    <Link to="/privacy-policy" className="terms-inline-link">
                      Privacy Policy
                    </Link>.
                  </strong>
                </div>
              </div>

              <p className="terms-paragraph">
                Your access to and use of the Service is conditioned on Your acceptance of and compliance with these Terms and Conditions. These Terms and Conditions apply to all visitors, users and others who access or use the Service.
              </p>
              <p className="terms-paragraph">
                By accessing or using the Service You agree to be bound by these Terms and Conditions. If You disagree with any part of these Terms and Conditions then You may not access the Service.
              </p>
              <p className="terms-paragraph">
                You represent that you are over the age of 18. The Company does not permit those under 18 to use the Service.
              </p>
              <p className="terms-paragraph">
                Your access to and use of the Service is also conditioned on Your acceptance of and compliance with the Privacy Policy of the Company. Our Privacy Policy describes Our policies and procedures on the collection, use and disclosure of Your personal information when You use the Application or the Website and tells You about Your privacy rights and how the law protects You. Please read Our Privacy Policy carefully before using Our Service.
              </p>
            </div>
          </section>

          {/* Section 3: Links to Other Websites */}
          <section id="links-to-other-websites" className="terms-card">
            <div className="terms-card-header">
              <div className="terms-section-icon-box box-purple">
                <FiExternalLink className="terms-section-icon" />
              </div>
              <h2 className="terms-card-title">
                3. Links to Other <span className="title-gradient">Websites</span>
              </h2>
            </div>

            <div className="terms-card-body">
              <p className="terms-paragraph">
                Our Service may contain links to third-party web sites or services that are not owned or controlled by the Company.
              </p>
              <p className="terms-paragraph">
                The Company has no control over, and assumes no responsibility for, the content, privacy policies, or practices of any third party web sites or services. You further acknowledge and agree that the Company shall not be responsible or liable, directly or indirectly, for any damage or loss caused or alleged to be caused by or in connection with the use of or reliance on any such content, goods or services available on or through any such web sites or services.
              </p>
              <p className="terms-paragraph">
                We strongly advise You to read the terms and conditions and privacy policies of any third-party web sites or services that You visit.
              </p>
            </div>
          </section>

          {/* Section 4: Termination */}
          <section id="termination" className="terms-card">
            <div className="terms-card-header">
              <div className="terms-section-icon-box box-rose">
                <FiPower className="terms-section-icon" />
              </div>
              <h2 className="terms-card-title">
                4. <span className="title-gradient">Termination</span>
              </h2>
            </div>

            <div className="terms-card-body">
              <p className="terms-paragraph">
                We may terminate or suspend Your access immediately, without prior notice or liability, for any reason whatsoever, including without limitation if You breach these Terms and Conditions.
              </p>
              <p className="terms-paragraph">
                Upon termination, Your right to use the Service will cease immediately.
              </p>
            </div>
          </section>

          {/* Section 5: Limitation of Liability */}
          <section id="limitation-of-liability" className="terms-card">
            <div className="terms-card-header">
              <div className="terms-section-icon-box box-amber">
                <FiShield className="terms-section-icon" />
              </div>
              <h2 className="terms-card-title">
                5. Limitation of <span className="title-gradient">Liability</span>
              </h2>
            </div>

            <div className="terms-card-body">
              <p className="terms-paragraph">
                Notwithstanding any damages that You might incur, the entire liability of the Company and any of its suppliers under any provision of this Terms and Your exclusive remedy for all of the foregoing shall be limited to the amount actually paid by You through the Service or 100 USD if You haven't purchased anything through the Service.
              </p>
              <p className="terms-paragraph">
                To the maximum extent permitted by applicable law, in no event shall the Company or its suppliers be liable for any special, incidental, indirect, or consequential damages whatsoever (including, but not limited to, damages for loss of profits, loss of data or other information, for business interruption, for personal injury, loss of privacy arising out of or in any way related to the use of or inability to use the Service, third-party software and/or third-party hardware used with the Service, or otherwise in connection with any provision of this Terms), even if the Company or any supplier has been advised of the possibility of such damages and even if the remedy fails of its essential purpose.
              </p>
              <p className="terms-paragraph">
                Some states do not allow the exclusion of implied warranties or limitation of liability for incidental or consequential damages, which means that some of the above limitations may not apply. In these states, each party's liability will be limited to the greatest extent permitted by law.
              </p>
            </div>
          </section>

          {/* Section 6: "AS IS" and "AS AVAILABLE" Disclaimer */}
          <section id="as-is-disclaimer" className="terms-card">
            <div className="terms-card-header">
              <div className="terms-section-icon-box box-amber">
                <FiAlertTriangle className="terms-section-icon" />
              </div>
              <h2 className="terms-card-title">
                6. "AS IS" and "AS AVAILABLE" <span className="title-gradient">Disclaimer</span>
              </h2>
            </div>

            <div className="terms-card-body">
              <p className="terms-paragraph">
                The Service is provided to You "AS IS" and "AS AVAILABLE" and with all faults and defects without warranty of any kind. To the maximum extent permitted under applicable law, the Company, on its own behalf and on behalf of its Affiliates and its and their respective licensors and service providers, expressly disclaims all warranties, whether express, implied, statutory or otherwise, with respect to the Service, including all implied warranties of merchantability, fitness for a particular purpose, title and non-infringement, and warranties that may arise out of course of dealing, course of performance, usage or trade practice. Without limitation to the foregoing, the Company provides no warranty or undertaking, and makes no representation of any kind that the Service will meet Your requirements, achieve any intended results, be compatible or work with any other software, applications, systems or services, operate without interruption, meet any performance or reliability standards or be error free or that any errors or defects can or will be corrected.
              </p>
              <p className="terms-paragraph">
                Without limiting the foregoing, neither the Company nor any of the company's provider makes any representation or warranty of any kind, express or implied: (i) as to the operation or availability of the Service, or the information, content, and materials or products included thereon; (ii) that the Service will be uninterrupted or error-free; (iii) as to the accuracy, reliability, or currency of any information or content provided through the Service; or (iv) that the Service, its servers, the content, or e-mails sent from or on behalf of the Company are free of viruses, scripts, trojan horses, worms, malware, timebombs or other harmful components.
              </p>
              <p className="terms-paragraph">
                Some jurisdictions do not allow the exclusion of certain types of warranties or limitations on applicable statutory rights of a consumer, so some or all of the above exclusions and limitations may not apply to You. But in such a case the exclusions and limitations set forth in this section shall be applied to the greatest extent enforceable under applicable law.
              </p>
            </div>
          </section>

          {/* Section 7: Governing Law */}
          <section id="governing-law" className="terms-card">
            <div className="terms-card-header">
              <div className="terms-section-icon-box box-cyan">
                <FiGlobe className="terms-section-icon" />
              </div>
              <h2 className="terms-card-title">
                7. Governing <span className="title-gradient">Law</span>
              </h2>
            </div>

            <div className="terms-card-body">
              <p className="terms-paragraph">
                The laws of India govern these Terms. Your use may also be subject to other laws depending on your location.
              </p>
            </div>
          </section>

          {/* Section 8: Dispute Resolution */}
          <section id="dispute-resolution" className="terms-card">
            <div className="terms-card-header">
              <div className="terms-section-icon-box box-indigo">
                <FiUsers className="terms-section-icon" />
              </div>
              <h2 className="terms-card-title">
                8. Dispute <span className="title-gradient">Resolution</span>
              </h2>
            </div>

            <div className="terms-card-body">
              <p className="terms-paragraph">
                In the event of any dispute, You agree to first try to resolve it informally by contacting Us.
              </p>
            </div>
          </section>

          {/* Section 9: EU Users */}
          <section id="eu-users" className="terms-card">
            <div className="terms-card-header">
              <div className="terms-section-icon-box box-teal">
                <FiCompass className="terms-section-icon" />
              </div>
              <h2 className="terms-card-title">
                9. EU <span className="title-gradient">Users</span>
              </h2>
            </div>

            <div className="terms-card-body">
              <p className="terms-paragraph">
                If You are an EU consumer, you will benefit from mandatory provisions of the law of the country in which You reside.
              </p>
            </div>
          </section>

          {/* Section 10: United States Legal Compliance */}
          <section id="us-legal-compliance" className="terms-card">
            <div className="terms-card-header">
              <div className="terms-section-icon-box box-blue">
                <FiCheckSquare className="terms-section-icon" />
              </div>
              <h2 className="terms-card-title">
                10. United States Legal <span className="title-gradient">Compliance</span>
              </h2>
            </div>

            <div className="terms-card-body">
              <p className="terms-paragraph">
                You represent and warrant that (i) You are not located in a country that is subject to the United States government embargo, or that has been designated by the United States government as a "terrorist supporting" country, and (ii) You are not listed on any United States government list of prohibited or restricted parties.
              </p>
            </div>
          </section>

          {/* Section 11: Severability and Waiver */}
          <section id="severability-waiver" className="terms-card">
            <div className="terms-card-header">
              <div className="terms-section-icon-box box-purple">
                <FiLayers className="terms-section-icon" />
              </div>
              <h2 className="terms-card-title">
                11. Severability and <span className="title-gradient">Waiver</span>
              </h2>
            </div>

            <div className="terms-card-body">
              <div className="terms-subblock">
                <h3 className="terms-subblock-title">Severability:</h3>
                <p className="terms-paragraph">
                  If any provision of these Terms is held to be unenforceable or invalid, such provision will be changed and interpreted to accomplish the objectives of such provision to the greatest extent possible under applicable law and the remaining provisions will continue in full force and effect.
                </p>
              </div>

              <div className="terms-subblock">
                <h3 className="terms-subblock-title">Waiver:</h3>
                <p className="terms-paragraph">
                  Except as provided herein, the failure to exercise a right or to require performance of an obligation under these Terms shall not affect a party's ability to exercise such right or require such performance at any time thereafter nor shall the waiver of a breach constitute a waiver of any subsequent breach.
                </p>
              </div>
            </div>
          </section>

          {/* Section 12: Translation Interpretation */}
          <section id="translation-interpretation" className="terms-card">
            <div className="terms-card-header">
              <div className="terms-section-icon-box box-teal">
                <FiBookOpen className="terms-section-icon" />
              </div>
              <h2 className="terms-card-title">
                12. Translation <span className="title-gradient">Interpretation</span>
              </h2>
            </div>

            <div className="terms-card-body">
              <p className="terms-paragraph">
                If these Terms have been translated, the original English version shall prevail.
              </p>
            </div>
          </section>

          {/* Section 13: Changes to Terms and Conditions */}
          <section id="changes-to-terms" className="terms-card">
            <div className="terms-card-header">
              <div className="terms-section-icon-box box-amber">
                <FiRefreshCw className="terms-section-icon" />
              </div>
              <h2 className="terms-card-title">
                13. Changes to Terms and <span className="title-gradient">Conditions</span>
              </h2>
            </div>

            <div className="terms-card-body">
              <p className="terms-paragraph">
                We reserve the right, at Our sole discretion, to modify or replace these Terms at any time. If a revision is material We will make reasonable efforts to provide at least 30 days' notice prior to any new terms taking effect. What constitutes a material change will be determined at Our sole discretion.
              </p>
              <p className="terms-paragraph">
                By continuing to access or use Our Service after those revisions become effective, You agree to be bound by the revised terms. If You do not agree to the new terms, in whole or in part, please stop using the website and the Service.
              </p>
            </div>
          </section>

          {/* Section 14: Contact Us */}
          <section id="contact-us" className="terms-card">
            <div className="terms-card-header">
              <div className="terms-section-icon-box box-blue">
                <FiMail className="terms-section-icon" />
              </div>
              <h2 className="terms-card-title">
                14. Contact <span className="title-gradient">Us</span>
              </h2>
            </div>

            <div className="terms-card-body">
              <p className="terms-paragraph">
                If you have any questions about these Terms and Conditions, You can contact us:
              </p>
              <div className="terms-contact-card">
                <div className="contact-card-icon-box">
                  <FiMail className="contact-mail-icon" />
                </div>
                <div className="contact-card-details">
                  <div className="contact-card-label">By visiting this page on our website:</div>
                  <a
                    href="https://praveen-kumar-b.web.app/contact"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="contact-card-button"
                  >
                    <span>Contact If Any Queries</span>
                    <FiChevronRight className="btn-arrow" />
                  </a>
                </div>
              </div>
            </div>
          </section>

          {/* Section 15: View Other Legal Policies */}
          <section id="view-other-policies" className="terms-card">
            <div className="terms-card-header">
              <div className="terms-section-icon-box box-purple">
                <FiShield className="terms-section-icon" />
              </div>
              <h2 className="terms-card-title">
                15. View Other Legal <span className="title-gradient">Policies</span>
              </h2>
            </div>

            <div className="terms-card-body">
              <div className="related-policies-grid">
                <Link to="/privacy-policy" className="policy-link-card">
                  <span className="policy-arrow">➔</span>
                  <div className="policy-info">
                    <span className="policy-name">View Privacy Policy</span>
                    <span className="policy-sub">Data & Privacy Protection</span>
                  </div>
                  <FiChevronRight className="policy-link-chevron" />
                </Link>

                <Link to="/terms-conditions" className="policy-link-card active">
                  <span className="policy-arrow">➔</span>
                  <div className="policy-info">
                    <span className="policy-name">View Terms & Conditions</span>
                    <span className="policy-sub">Current Document</span>
                  </div>
                  <FiChevronRight className="policy-link-chevron" />
                </Link>

                <a
                  href="https://policiespraveenkumarb.netlify.app/cookie-policy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="policy-link-card"
                >
                  <span className="policy-arrow">➔</span>
                  <div className="policy-info">
                    <span className="policy-name">View Cookie Policy</span>
                    <span className="policy-sub">Tracking & Preferences</span>
                  </div>
                  <FiChevronRight className="policy-link-chevron" />
                </a>
              </div>
            </div>
          </section>
        </main>
      </div>

      {/* Floating Back-To-Top Button */}
      {showBackToTop && (
        <button
          className="terms-back-to-top"
          onClick={scrollToTop}
          title="Back to Top"
          aria-label="Back to Top"
        >
          <FiArrowUp />
        </button>
      )}
    </div>
  );
};

export default TermsConditions;
